import { Button, Icon, Menu, type MenuItem } from '@ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { BatchFormDialog } from '../../harvest/components/BatchFormDialog';
import { FillingDialog } from '../../harvest/components/FillingDialog';
import type { Permission } from '../../identity/api';
import { useSession } from '../../identity/session';
import { EntryFormDialog } from '../../finance/components/EntryFormDialog';
import { SaleFormDialog } from '../../sales/components/SaleFormDialog';
import { TaskFormDialog } from '../../tasks/components/TaskFormDialog';
import { ColonyFormDialog } from './ColonyFormDialog';
import { FeedingDialog } from './FeedingDialog';
import { LocationFormDrawer } from './LocationFormDrawer';
import { MiteCountDialog } from './MiteCountDialog';
import { TreatmentDialog } from './TreatmentDialog';

/** `?aktion=` of the app shortcuts (manifest) and the action it opens. */
const SHORTCUTS: Record<string, { action: 'sale' | 'treatment' | 'task'; permission: Permission }> =
  {
    verkauf: { action: 'sale', permission: 'sales.write' },
    behandlung: { action: 'treatment', permission: 'treatments.write' },
    aufgabe: { action: 'task', permission: 'tasks.write' },
  };

type Action =
  | 'treatment'
  | 'feeding'
  | 'mite'
  | 'colony'
  | 'location'
  | 'harvest'
  | 'filling'
  | 'sale'
  | 'task'
  | 'expense'
  | null;

/** "+" for frequent records from anywhere in the app (WEB-006). */
export function QuickActions() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [action, setAction] = useState<Action>(null);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const shortcut = params.get('aktion');
  useEffect(() => {
    if (shortcut === null) return;
    const entry = SHORTCUTS[shortcut];
    if (entry && can(entry.permission)) setAction(entry.action);
    setParams(
      (p) => {
        p.delete('aktion');
        return p;
      },
      { replace: true },
    );
  }, [shortcut, can, setParams]);
  const items: MenuItem[] = [
    ...(can('treatments.write')
      ? [
          {
            key: 'treatment',
            label: t('apiary.quick.treatment'),
            onSelect: () => setAction('treatment'),
          },
        ]
      : []),
    ...(can('feedings.write')
      ? [
          {
            key: 'feeding',
            label: t('apiary.quick.feeding'),
            onSelect: () => setAction('feeding'),
          },
        ]
      : []),
    ...(can('treatments.write')
      ? [
          {
            key: 'mite',
            label: t('apiary.quick.miteCount'),
            onSelect: () => setAction('mite'),
          },
        ]
      : []),
    ...(can('colonies.write')
      ? [
          {
            key: 'colony',
            label: t('apiary.quick.colony'),
            onSelect: () => setAction('colony'),
          },
        ]
      : []),
    ...(can('locations.write')
      ? [
          {
            key: 'location',
            label: t('apiary.quick.location'),
            onSelect: () => setAction('location'),
          },
        ]
      : []),
  ];
  if (can('harvest.write')) {
    items.push({
      key: 'harvest',
      label: t('harvest.quick.harvest'),
      onSelect: () => setAction('harvest'),
    });
  }
  if (can('stock.write')) {
    items.push({
      key: 'filling',
      label: t('harvest.quick.filling'),
      onSelect: () => setAction('filling'),
    });
  }
  if (can('sales.write')) {
    items.push({
      key: 'sale',
      label: t('sales.quick.sale'),
      onSelect: () => setAction('sale'),
    });
  }
  if (can('tasks.write')) {
    items.push({
      key: 'task',
      label: t('tasks.new'),
      onSelect: () => setAction('task'),
    });
  }
  if (can('finance.write')) {
    items.push({
      key: 'expense',
      label: t('finance.quick.expense'),
      onSelect: () => setAction('expense'),
    });
  }
  if (items.length === 0) return null;
  const close = () => setAction(null);
  return (
    <>
      <Menu
        items={items}
        align="end"
        trigger={(p) => (
          <Button
            {...p}
            variant="plain"
            aria-label={t('apiary.quick.label')}
            iconStart={<Icon name="plus" size="lg" />}
          />
        )}
      />
      {action === 'treatment' && <TreatmentDialog open onClose={close} />}
      {action === 'feeding' && <FeedingDialog open onClose={close} />}
      {action === 'mite' && <MiteCountDialog open onClose={close} />}
      {action === 'colony' && <ColonyFormDialog open onClose={close} />}
      {action === 'location' && <LocationFormDrawer open onClose={close} />}
      {action === 'harvest' && (
        <BatchFormDialog open onClose={close} onSaved={(b) => void navigate(`/ernte/${b.id}`)} />
      )}
      {action === 'filling' && <FillingDialog open onClose={close} />}
      {action === 'task' && (
        <TaskFormDialog open onClose={close} onSaved={(x) => void navigate(`/aufgaben/${x.id}`)} />
      )}
      {action === 'expense' && (
        <EntryFormDialog
          open
          kind="expense"
          onClose={close}
          onSaved={(x) => void navigate(`/finanzen/buchungen/${x.id}`)}
        />
      )}
      {action === 'sale' && (
        <SaleFormDialog open onClose={close} onSaved={(s) => void navigate(`/verkauf/${s.id}`)} />
      )}
    </>
  );
}
